import { useState } from 'react'

import axios from 'axios'

import { envs } from '@/envs'
import { getMs } from '@/helpers'
import { isCordovaApp } from '@/helpers'
import { cordovaHttpRequest, isCordovaHttpAvailable } from '@/helpers/cordova-http'


export interface T_UpdateResponse {
  hasUpdate: boolean
  latestVersion: string
  isMandatory: boolean
  description: string
  downloadUrl: string
}

type T_UpdateInfoPayload = Partial<T_UpdateResponse> & {
  // allow alternative keys
  version?: string
  url?: string
  mandatory?: boolean
}

const parseSemver = (v: string): number[] | null => {
  const cleaned = String(v || '').trim().replace(/^v/i, '')
  const parts = cleaned.split('.').map((x) => Number(x))
  if (!parts.length || parts.some((n) => !Number.isFinite(n))) return null
  return parts
}

const isNewerVersion = (latest: string, current: string): boolean => {
  const a = parseSemver(latest)
  const b = parseSemver(current)
  if (!a || !b) return latest !== current

  const maxLen = Math.max(a.length, b.length)
  for (let i = 0; i < maxLen; i++) {
    const av = a[i] ?? 0
    const bv = b[i] ?? 0
    if (av > bv) return true
    if (av < bv) return false
  }
  return false
}

const getCurrentAppVersion = async (): Promise<string> => {
  // In Cordova we prefer real app version from plugin (if available)
  // cordova-plugin-app-version exposes `cordova.getAppVersion.getVersionNumber()`
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const c: any = (globalThis as any).cordova
  const getter = c?.getAppVersion?.getVersionNumber
  if (typeof getter === 'function') {
    try {
      const v = await getter()
      if (typeof v === 'string' && v.trim()) return v.trim()
    } catch {
      // ignore and fallback
    }
  }

  // Fallback to build-time version (package.json injected via Vite define)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const v = (globalThis as any).__APP_VERSION__ as string | undefined
  return (typeof v === 'string' && v.trim()) ? v.trim() : '0.0.0'
}

export const useCheckAppVersion = () => {
  const [loading, setLoading] = useState(false)

  const checkUpdate = async (): Promise<T_UpdateResponse | null> => {
    // If not cordova environment, return null immediately
    if (!isCordovaApp) {
      return null
    }

    setLoading(true)

    try {
      const currentVersion = await getCurrentAppVersion()

      // Fetch update info JSON from server (must be hosted by backend/nginx/CDN)
      const url = envs.CORDOVA_UPDATE_INFO_URL
      const cacheBuster = url.includes('?') ? `&_cb=${Date.now()}` : `?_cb=${Date.now()}`
      const finalUrl = `${url}${cacheBuster}`

      const payload: T_UpdateInfoPayload = isCordovaApp && isCordovaHttpAvailable()
        ? await cordovaHttpRequest<T_UpdateInfoPayload>('GET', finalUrl)
        : (await axios.get<T_UpdateInfoPayload>(finalUrl, { timeout: getMs('10s') })).data

      const latestVersion =
        String(payload.latestVersion || payload.version || '').trim()

      if (!latestVersion) {
        // no data - treat as "no update info"
        return null
      }

      const downloadUrlRaw =
        String(payload.downloadUrl || payload.url || '').trim()

      const downloadUrl = downloadUrlRaw && !downloadUrlRaw.startsWith('http')
        ? `${envs.API_DOMAIN}${downloadUrlRaw.startsWith('/') ? '' : '/'}${downloadUrlRaw}`
        : downloadUrlRaw

      const isMandatory =
        Boolean(payload.isMandatory ?? payload.mandatory ?? false)

      const description = String(payload.description || '').trim()

      const hasUpdate = isNewerVersion(latestVersion, currentVersion)

      return {
        hasUpdate,
        latestVersion,
        isMandatory,
        description,
        downloadUrl,
      }
    } catch (error) {
      console.error('Failed to check for updates', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  return { checkUpdate, loading }
}
